/**
 * Dev persona auth (LOCAL DEV / E2E ONLY). Active when /config returns
 * authProvider "dev" (AUTH_PROVIDER=dev in web/.env).
 *
 * Instead of an IdP login, the user picks a persona (admin, member, viewer,
 * deactivated...). The backend mints a real Anthm-issuer token for it, so
 * every request goes through the real auth + permission path. The real gate
 * is the backend: /api/v1/dev/* only exists there with DEV_AUTH_ENABLED=true.
 *
 * The chosen persona lives in localStorage (DEV_PERSONA_STORAGE_KEY) so it
 * survives reloads, and Playwright can pick one with a single init script.
 */

import { useQueryClient } from "@tanstack/react-query";
import { createContext, type ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { setTokenProvider } from "@/api";
import { AuthContext, type AuthContextValue } from "../AuthContext";
import { type DevPersona, type DevToken, fetchDevToken } from "./devAuthApi";
import { DEV_PERSONA_STORAGE_KEY } from "./storage";

export { DEV_PERSONA_STORAGE_KEY };

/** Refresh a minted token this long before it expires. */
const EXPIRY_MARGIN_MS = 60_000;

interface DevAuthContextValue {
  persona: DevPersona | null;
  signInAs: (personaKey: string) => Promise<void>;
}

const DevAuthContext = createContext<DevAuthContextValue | null>(null);

/** Dev-only extras (current persona, switching). Null outside dev auth. */
export function useDevAuth(): DevAuthContextValue | null {
  return useContext(DevAuthContext);
}

interface CachedToken {
  token: DevToken;
  expiresAt: number;
}

export function DevAuthProvider({ children }: Readonly<{ children: ReactNode }>) {
  const queryClient = useQueryClient();
  const [personaKey, setPersonaKey] = useState<string | null>(() => localStorage.getItem(DEV_PERSONA_STORAGE_KEY));
  const [persona, setPersona] = useState<DevPersona | null>(null);
  const [error, setError] = useState<unknown>(null);
  const cache = useRef<CachedToken | null>(null);

  const mint = useCallback(async (key: string): Promise<DevToken> => {
    const token = await fetchDevToken(key);
    cache.current = { token, expiresAt: Date.now() + token.expiresIn * 1000 };
    return token;
  }, []);

  const getAccessToken = useCallback(async (): Promise<string | null> => {
    if (!personaKey) return null;
    const cached = cache.current;
    if (cached && cached.token.persona.key === personaKey && cached.expiresAt - EXPIRY_MARGIN_MS > Date.now()) {
      return cached.token.accessToken;
    }
    return (await mint(personaKey)).accessToken;
  }, [personaKey, mint]);

  useEffect(() => {
    setTokenProvider(getAccessToken);
    return () => setTokenProvider(null);
  }, [getAccessToken]);

  // Resolve the stored persona on load (and after switching) before rendering the app.
  useEffect(() => {
    if (!personaKey) return;
    let cancelled = false;
    mint(personaKey)
      .then((t) => !cancelled && setPersona(t.persona))
      .catch((e: unknown) => {
        if (cancelled) return;
        // Unknown persona or backend without DEV_AUTH: drop back to the picker.
        localStorage.removeItem(DEV_PERSONA_STORAGE_KEY);
        setPersonaKey(null);
        setError(e);
      });
    return () => {
      cancelled = true;
    };
  }, [personaKey, mint]);

  const switchTo = useCallback(
    (key: string | null) => {
      // A different identity must never see the previous one's cached data.
      queryClient.clear();
      cache.current = null;
      setPersona(null);
      setError(null);
      if (key) localStorage.setItem(DEV_PERSONA_STORAGE_KEY, key);
      else localStorage.removeItem(DEV_PERSONA_STORAGE_KEY);
      setPersonaKey(key);
    },
    [queryClient],
  );

  const signInAs = useCallback(
    (key: string) => {
      switchTo(key);
      return Promise.resolve();
    },
    [switchTo],
  );

  const auth = useMemo<AuthContextValue>(
    () => ({
      isAuthenticated: persona !== null,
      isLoading: personaKey !== null && persona === null && error === null,
      accountName: persona?.name ?? null,
      // The sign-in page renders the persona picker for dev auth; nothing to do here.
      login: () => Promise.resolve(),
      logout: () => {
        switchTo(null);
        return Promise.resolve();
      },
    }),
    [persona, personaKey, error, switchTo],
  );

  const dev = useMemo<DevAuthContextValue>(() => ({ persona, signInAs }), [persona, signInAs]);

  return (
    <AuthContext.Provider value={auth}>
      <DevAuthContext.Provider value={dev}>{children}</DevAuthContext.Provider>
    </AuthContext.Provider>
  );
}
